import { useState, type ReactNode } from 'react';
import { Filter, Plus, SquarePen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useGetShippingRoutes,
  useGetPorts,
  useGetShippingRouteCategories,
  useGetLogisticsIncoterms,
} from '~/api/logistics';
import type { RouteSort } from '~/hooks/usePaginatedQuery';
import { Button } from '~/components/ui/buttons/Button';
import { FilterSidebar } from '~/components/ui/FilterSidebar';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import {
  Table,
  type TableColumn,
  type TableRow,
  useTable,
} from '~/components/ui/table';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { ShippingRouteModal, type ModalMode } from './ShippingRouteModal';
import type {
  GetShippingRoutesQuery,
  LogisticsIncoterm,
  ShippingRouteFilterInput,
  ShippingRouteStatus,
} from '~/graphql/graphql';

type ShippingRoute =
  GetShippingRoutesQuery['shippingRoutes']['content'][number];

const COMBO_INCOTERMS: LogisticsIncoterm[] = [
  'FCA_COMBO',
  'DDP_COMBO',
  'DAP_COMBO',
];

const STATUS_OPTIONS: ShippingRouteStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'REQUESTED',
  'REQUEST_DENIED',
];

const SORT_FIELDS: Partial<Record<string, string>> = {
  portOfLoading: 'portOfLoading.city',
  portOfDischarge: 'portOfDischarge.city',
  freightCost: 'freightCost40Ft',
  expirationDate: 'expirationDate',
  status: 'status',
};

function getRouteName(route: ShippingRoute): string {
  const parts: string[] = [];
  if (
    route.incotermLoading != null &&
    COMBO_INCOTERMS.includes(route.incotermLoading) &&
    route.originDelivery?.code
  ) {
    parts.push(route.originDelivery.code);
  }
  if (route.portOfLoading?.code) parts.push(route.portOfLoading.code);
  if (route.portOfDischarge?.code) parts.push(route.portOfDischarge.code);
  if (
    route.incotermDischarge != null &&
    COMBO_INCOTERMS.includes(route.incotermDischarge) &&
    route.destinationDelivery?.code
  ) {
    parts.push(route.destinationDelivery.code);
  }
  return parts.join(' - ');
}

function StatusBadge({
  status,
  label,
}: {
  status: ShippingRouteStatus;
  label: string;
}): ReactNode {
  const colours: Record<ShippingRouteStatus, string> = {
    ACTIVE: 'text-success-700 bg-success-100',
    INACTIVE: 'text-neut-500 bg-neut-100',
    REQUESTED: 'text-warning-600 bg-warning-100',
    REQUEST_DENIED: 'text-error-500 bg-error-100',
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap ${colours[status]}`}
    >
      {label}
    </span>
  );
}

function ScopeBadge({
  label,
  isOrg,
}: {
  label: string;
  isOrg: boolean;
}): ReactNode {
  const cls = isOrg ? 'bg-prim-50 text-prim' : 'bg-neut-100 text-neut-600';
  return (
    <span
      className={`text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
}

function formatPortCell(
  port: ShippingRoute['portOfLoading'] | null | undefined
): string {
  if (!port) return '—';
  const parts: string[] = [];
  if (port.city) parts.push(port.city);
  if (port.country) parts.push(port.country);
  return parts.length > 0 ? parts.join(', ') : port.name;
}

function formatMoney(
  money: { amount: number; currency: string } | null | undefined
): string {
  if (!money) return '—';
  return `${money.amount.toLocaleString()} ${money.currency}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export function ShippingRoutesTable() {
  const { t } = useTranslation('logistics');

  const [orgOnly, setOrgOnly] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ShippingRouteFilterInput>(
    {}
  );
  const [pendingFilter, setPendingFilter] = useState<ShippingRouteFilterInput>(
    {}
  );

  const [sort, setSort] = useState<RouteSort | undefined>();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [selectedRoute, setSelectedRoute] = useState<
    ShippingRoute | undefined
  >();

  const activeFilterCount = Object.values(activeFilter).filter(
    (v) => v !== undefined && v !== ''
  ).length;

  const liveFilter: ShippingRouteFilterInput = orgOnly
    ? { ...activeFilter, organizationOnly: true }
    : activeFilter;

  const {
    data: allRoutes,
    sentinelRef,
    isLoading,
    isLoadingMore,
    pagination,
  } = usePaginatedQuery({
    fetchHook: useGetShippingRoutes,
    select: (data) => data?.shippingRoutes,
    options: { pageSize: 20, filters: liveFilter, sort },
  });

  const hasMorePages =
    pagination != null ? pagination.number < pagination.totalPages - 1 : false;
  const { data: portsData } = useGetPorts();
  const { data: categoriesData } = useGetShippingRouteCategories();
  const { data: incotermsData } = useGetLogisticsIncoterms();

  const ports = portsData?.ports ?? [];
  const categories = categoriesData?.shippingRouteCategories ?? [];
  const incotermOptions = incotermsData?.logisticsIncoterms ?? [];

  const tableControls = useTable({
    onSort: (key, direction) => {
      const field = SORT_FIELDS[key];
      setSort(
        field && direction
          ? [{ field, direction: direction.toUpperCase() as 'ASC' | 'DESC' }]
          : undefined
      );
    },
  });

  const openModal = (mode: ModalMode, route?: ShippingRoute) => {
    setModalMode(mode);
    setSelectedRoute(route);
    setModalOpen(true);
  };

  const applyFilter = () => {
    setActiveFilter(pendingFilter);
    setFilterPanelOpen(false);
  };

  const clearFilter = () => {
    setPendingFilter({});
    setActiveFilter({});
  };

  const setFilterField = (
    field: keyof ShippingRouteFilterInput,
    value: string
  ) => {
    setPendingFilter((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const portOptions = ports.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const columns: TableColumn[] = [
    {
      key: 'route',
      header: t('logistics.shipping_routes.table.route'),
      width: 8,
      stickTo: 'left',
    },
    {
      key: 'incotermLoading',
      header: t('logistics.shipping_routes.table.incoterm_loading'),
      width: 7,
    },
    {
      key: 'portOfLoading',
      header: t('logistics.shipping_routes.table.port_of_loading'),
      width: 9,
      isSortable: true,
    },
    {
      key: 'incotermDischarge',
      header: t('logistics.shipping_routes.table.incoterm_discharge'),
      width: 7,
    },
    {
      key: 'portOfDischarge',
      header: t('logistics.shipping_routes.table.port_of_discharge'),
      width: 9,
      isSortable: true,
    },
    {
      key: 'freightCost',
      header: t('logistics.shipping_routes.table.freight_cost'),
      width: 7,
      isSortable: true,
    },
    {
      key: 'freeDays',
      header: t('logistics.shipping_routes.table.free_days'),
      width: 5,
    },
    {
      key: 'expirationDate',
      header: t('logistics.shipping_routes.table.expiration_date'),
      width: 7,
      isSortable: true,
    },
    {
      key: 'status',
      header: t('logistics.shipping_routes.table.status'),
      width: 6,
      isSortable: true,
    },
    {
      key: 'category',
      header: t('logistics.shipping_routes.table.category'),
      width: 7,
    },
    {
      key: 'scope',
      header: t('logistics.shipping_routes.table.scope'),
      width: 5,
    },
    {
      key: 'actions',
      header: t('logistics.shipping_routes.table.actions'),
      width: 4,
      stickTo: 'right',
    },
  ];

  const rows: TableRow[] = allRoutes.map((route, idx) => {
    const isOrgOwned = route.organizationId !== null;
    return {
      id: { value: route.id },
      route: {
        value: (
          <span className="font-medium whitespace-nowrap">
            {getRouteName(route)}
          </span>
        ),
      },
      incotermLoading: { value: route.incotermLoading },
      portOfLoading: { value: formatPortCell(route.portOfLoading) },
      originDelivery: { value: formatPortCell(route.originDelivery) },
      incotermDischarge: { value: route.incotermDischarge },
      portOfDischarge: { value: formatPortCell(route.portOfDischarge) },
      destinationDelivery: { value: formatPortCell(route.destinationDelivery) },
      freightCost: { value: formatMoney(route.freightCost) },
      freeDays: {
        value: route.freeDays != null ? String(route.freeDays) : '—',
      },
      expirationDate: { value: formatDate(route.expirationDate) },
      status: {
        value: (
          <StatusBadge
            status={route.status}
            label={t(`logistics.shipping_routes.status.${route.status}`)}
          />
        ),
      },
      category: {
        value: route.category ?? '—',
      },
      scope: {
        value: (
          <ScopeBadge
            isOrg={isOrgOwned}
            label={
              isOrgOwned
                ? t('logistics.shipping_routes.scope.org')
                : t('logistics.shipping_routes.scope.platform')
            }
          />
        ),
      },
      actions: {
        value: isOrgOwned ? (
          <button
            data-testid={`edit-route-${idx}`}
            className="cursor-pointer text-neut-sec hover:text-neut-prim transition-colors"
            onClick={() => openModal('edit', route)}
            aria-label={`${t('logistics.shipping_routes.modal.edit_title')} ${getRouteName(route)}`}
            title={`${t('logistics.shipping_routes.modal.edit_title')} ${getRouteName(route)}`}
          >
            <SquarePen size={16} />
          </button>
        ) : null,
      },
    };
  });

  return (
    <>
      <ShippingRouteModal
        key={`${modalMode}-${selectedRoute?.id ?? 'new'}`}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        route={selectedRoute}
        ports={ports}
      />

      <div className="flex gap-4">
        {/* Main content */}
        <div className="flex-1 bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-neut-prim">
                {t('logistics.shipping_routes.title')}
              </h2>
              <p className="text-xs text-neut-sec mt-1">
                {t('logistics.shipping_routes.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center bg-neut-200 rounded-lg p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setOrgOnly(false)}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${!orgOnly ? 'bg-white text-neut-prim shadow-sm' : 'text-neut-sec hover:text-neut-prim'}`}
                >
                  {t('logistics.shipping_routes.scope_toggle.all')}
                </button>
                <button
                  type="button"
                  onClick={() => setOrgOnly(true)}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${orgOnly ? 'bg-white text-neut-prim shadow-sm' : 'text-neut-sec hover:text-neut-prim'}`}
                >
                  {t('logistics.shipping_routes.scope_toggle.org')}
                </button>
              </div>
              <Button
                size="sm"
                width="auto"
                onClick={() => openModal('create')}
              >
                <Plus size={16} className="mr-1" />
                {t('logistics.shipping_routes.add_route')}
              </Button>
              <Button
                size="sm"
                width="auto"
                onClick={() => setFilterPanelOpen((o) => !o)}
              >
                <Filter size={16} className="mr-1" />
                {t('logistics.common.filter')}
                {activeFilterCount > 0 && (
                  <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 ml-1 text-xs font-semibold rounded-full bg-prim text-white">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table
              minWidth={95}
              rows={rows}
              columns={columns}
              tableControls={tableControls}
              emptyMessage={t('logistics.shipping_routes.empty')}
              totalRowsCount={pagination?.totalElements ?? 0}
            />
            {hasMorePages && <div ref={sentinelRef} />}
            {(isLoading || (isLoadingMore && hasMorePages)) && (
              <PageLoadingSpinner name="routes" />
            )}
            {!hasMorePages && !isLoading && allRoutes.length > 0 && (
              <div className="flex justify-center py-4">
                <div className="text-sm text-neut-500">
                  {t('logistics.shipping_routes.no_more_routes')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterPanelOpen && (
          <FilterSidebar
            title={t('logistics.common.filter')}
            onClose={() => setFilterPanelOpen(false)}
            onApply={applyFilter}
            onClear={clearFilter}
          >
            <SearchableSelect
              label={t('logistics.shipping_routes.table.status')}
              name="status"
              value={(pendingFilter.status as string) ?? ''}
              onChange={(_name, value) => setFilterField('status', value)}
              options={[
                { value: '', label: '—' },
                ...STATUS_OPTIONS.map((s) => ({
                  value: s,
                  label: t(`logistics.shipping_routes.status.${s}`),
                })),
              ]}
            />

            <SearchableSelect
              label={t('logistics.shipping_routes.table.category')}
              name="category"
              value={(pendingFilter.category as string) ?? ''}
              onChange={(_name, value) => setFilterField('category', value)}
              options={[
                { value: '', label: '—' },
                ...categories.map((c) => ({ value: c, label: c })),
              ]}
            />

            <SearchableSelect
              label={t('logistics.shipping_routes.table.incoterm_loading')}
              name="incotermLoading"
              value={(pendingFilter.incotermLoading as string) ?? ''}
              onChange={(_name, value) =>
                setFilterField('incotermLoading', value)
              }
              options={[
                { value: '', label: '—' },
                ...incotermOptions.map((i) => ({ value: i, label: i })),
              ]}
            />

            <SearchableSelect
              label={t('logistics.shipping_routes.table.incoterm_discharge')}
              name="incotermDischarge"
              value={(pendingFilter.incotermDischarge as string) ?? ''}
              onChange={(_name, value) =>
                setFilterField('incotermDischarge', value)
              }
              options={[
                { value: '', label: '—' },
                ...incotermOptions.map((i) => ({ value: i, label: i })),
              ]}
            />

            <SearchableSelect
              label={t('logistics.shipping_routes.table.port_of_loading')}
              name="portOfLoadingId"
              value={(pendingFilter.portOfLoadingId as string) ?? ''}
              onChange={(_name, value) =>
                setFilterField('portOfLoadingId', value)
              }
              options={[{ value: '', label: '—' }, ...portOptions]}
            />

            <SearchableSelect
              label={t('logistics.shipping_routes.table.port_of_discharge')}
              name="portOfDischargeId"
              value={(pendingFilter.portOfDischargeId as string) ?? ''}
              onChange={(_name, value) =>
                setFilterField('portOfDischargeId', value)
              }
              options={[{ value: '', label: '—' }, ...portOptions]}
            />
          </FilterSidebar>
        )}
      </div>
    </>
  );
}
